/**
 * [PGF Draft Board Refresh v1.1 §E] WhiteboardDraftBoard - full static
 * render of the dry-erase whiteboard style. Consumed by StandaloneDraftBoard
 * when the resolved board style is "whiteboard".
 *
 *   • Dark stained-oak wood frame around a pure-white surface (#FFFFFF day
 *     / #F5EFE0 night + radial vignette).
 *   • Ghost brush streaks tiled behind everything at ~4% opacity.
 *   • Restrained ambient marks (§E.8): one smudge, one dust dot, one ghost
 *     of an erased word. Positions seeded by leagueId - static per league,
 *     not per render.
 *   • Team-name row (§E.3): red marker (#D02020) Permanent Marker ~26px,
 *     per-manager rotation ±1° seeded by user_id. Rendered as SVG <text>
 *     for consistent anti-aliasing.
 *   • Wobbly underscore line beneath the names (§E.3): SVG <path> with
 *     four small y-axis wobbles, corner to corner.
 *   • Sold picks (§E.4): stacked top-down in each manager column in draft
 *     order (rounds iterated). Line 1 = last name in position ink (§E.5),
 *     rotation ±0.8°. Line 2 = "POS · TEAM-BYE", line 3 = "$BID"
 *     (auction only, via showBid) - same color + rotation as line 1.
 *   • Position marker inks (§E.5) do NOT desaturate in night mode (§E.9).
 *   • CP-6 §E.7: new picks after mount reveal via a 2000ms simultaneous
 *     SVG clipPath rect that animates width 0 → full - both lines animate
 *     together. Existing picks on mount render statically (per §B.4
 *     mid-draft switch rule) - their IDs are snapshotted on first render
 *     and never animated.
 *   • [Stabilization fix - Feb 2026] Reverted to pre-sweep SVG <text>
 *     rendering for names (the sweep's HTML+useAutoFitText swap measured
 *     an auto-height wrapper with no fixed dimension, producing a 0px
 *     starting font size - names vanished entirely). SVG <text> always
 *     renders its glyphs; a `textLength` + `lengthAdjust` safety net is
 *     added on top so a name that's wider than its column compresses to
 *     fit instead of overflowing - re-applying "never overflow" without
 *     the JS measurement race.
 */
import React, { useRef } from "react";
import { motion } from "framer-motion";
import { WHITEBOARD_INK, WHITEBOARD_HEADER_RED } from "../../../utils/boardStyles";

const FRAME_PADDING = 24;
const HEADER_HEIGHT = 60;
const UNDERSCORE_HEIGHT = 14;
const ENTRY_HEIGHT = 72;
const MIN_COL_ENTRIES = 5;
// SVG viewBox width (user units) each entry/header text area is drawn
// into - text compresses via textLength if it would exceed this.
const ENTRY_VIEW_WIDTH = 190;
const HEADER_VIEW_WIDTH = 190;
// Horizontal center of an entry's SVG user space (viewBox is
// ENTRY_VIEW_WIDTH + 10 wide). Snake Board Fixes item 7 centers every
// pick line under its team column.
const ENTRY_CENTER_X = (ENTRY_VIEW_WIDTH + 10) / 2;

const MARKER_FONT = "'Permanent Marker', 'Kalam', cursive, sans-serif";

// Deterministic pseudo-random from a string seed (§D.5 pattern reused).
function seededHash(seed) {
  let h = 2166136261;
  const s = String(seed || "");
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
function seededSigned(seed, salt) {
  const h = seededHash(String(seed) + "|" + String(salt || ""));
  return ((h % 1000) / 500) - 1; // [-1, 1)
}

// Wobbly underscore path - corner to corner with four gentle y-drifts.
function underscorePath(width) {
  const y = 8;
  const segs = 5;
  let d = `M 0 ${y}`;
  for (let i = 1; i <= segs; i++) {
    const cx = (width / segs) * (i - 0.5);
    const cy = y + (i % 2 === 0 ? 2 : -2);
    const x = (width / segs) * i;
    d += ` Q ${cx.toFixed(1)} ${cy.toFixed(1)}, ${x.toFixed(1)} ${(y + ((i % 2) ? 1 : -1)).toFixed(1)}`;
  }
  return d;
}

// [Stabilization fix - Feb 2026] SVG text never overflows its column: if
// the text is long enough that it would likely exceed `maxWidthPx` at
// `fontSizePx` (rough avg-glyph-width heuristic for the marker font),
// force it to compress via `textLength` + `lengthAdjust`. Short/typical
// names are left alone (undefined textLength) so they render at their
// natural, un-stretched size.
function svgFitProps(text, fontSizePx, maxWidthPx) {
  const avgCharWidth = fontSizePx * 0.62;
  const estWidth = (text || "").length * avgCharWidth;
  if (estWidth > maxWidthPx) {
    return { textLength: maxWidthPx, lengthAdjust: "spacingAndGlyphs" };
  }
  return {};
}

/**
 * A single sold-pick entry - 3 lines (PGF Draft Board Sweep Item 6/5):
 * line 1 = last name, line 2 = POS · TEAM-BYE, line 3 = $price (auction
 * only, via showBid). Existing picks render static. New picks
 * (shouldAnimate=true) write in sequenced: line 1 reveals 0-1.15s, then
 * lines 2+3 reveal together 1.15-2.3s (Item 5). [Stabilization fix - Feb
 * 2026] Rendered as SVG <text> (pre-sweep known-good) - guarantees the
 * name always paints, with a `textLength` compression safety net for
 * names too long for the column (see `svgFitProps`).
 */
function WhiteboardEntry({ sale, shouldAnimate, showBid }) {
  const player = sale?.player || {};
  const pos = (player.position || "").toUpperCase();
  const ink = WHITEBOARD_INK[pos] || "#111111";
  const seedId = sale?.request_id || sale?.nomination_id || sale?.sale_id || player.id || player.name || "";
  const rot = seededSigned(seedId, "rot") * 0.8;
  const lastName = ((player.name || "").split(/\s+/).pop() || "").toUpperCase();
  const teamBye = `${(player.nfl_team || "FA").toUpperCase()}-${player.bye_week ?? "?"}`;
  const meta = `${pos} \u00B7 ${teamBye}`;
  const bid = sale?.paid ?? sale?.winning_bid ?? sale?.final_bid ?? 0;
  const clipId = `wb-entry-clip-${String(seedId).replace(/[^a-zA-Z0-9_-]/g, "_")}`;

  const line1 = (
    <text
      x={ENTRY_CENTER_X}
      y={26}
      textAnchor="middle"
      fill={ink}
      style={{ fontFamily: MARKER_FONT, fontSize: 22, letterSpacing: "0.02em", textTransform: "uppercase" }}
      {...svgFitProps(lastName, 22, ENTRY_VIEW_WIDTH - 16)}
    >
      {lastName}
    </text>
  );
  const line23 = (
    <>
      <text
        x={ENTRY_CENTER_X}
        y={44}
        textAnchor="middle"
        fill={ink}
        fillOpacity={0.9}
        style={{ fontFamily: MARKER_FONT, fontSize: 13, letterSpacing: "0.02em" }}
        {...svgFitProps(meta, 13, ENTRY_VIEW_WIDTH - 16)}
      >
        {meta}
      </text>
      {showBid && (
        <text
          x={ENTRY_CENTER_X}
          y={62}
          textAnchor="middle"
          fill={ink}
          fillOpacity={0.92}
          style={{ fontFamily: MARKER_FONT, fontSize: 14, letterSpacing: "0.02em" }}
        >
          ${bid}
        </text>
      )}
    </>
  );

  return (
    <div
      className="w-full"
      style={{ height: ENTRY_HEIGHT, position: "relative" }}
      data-testid={`whiteboard-entry-${seedId}`}
    >
      <svg
        width="100%"
        height={ENTRY_HEIGHT}
        viewBox={`0 0 ${ENTRY_VIEW_WIDTH + 10} ${ENTRY_HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ overflow: "visible" }}
      >
        <g transform={`rotate(${rot.toFixed(2)} 95 ${ENTRY_HEIGHT / 2})`}>
          {shouldAnimate ? (
            <>
              <defs>
                <clipPath id={`${clipId}-1`}>
                  <motion.rect
                    x={0}
                    y={0}
                    height={30}
                    initial={{ width: 0 }}
                    animate={{ width: ENTRY_VIEW_WIDTH + 10 }}
                    transition={{ duration: 1.15, ease: "easeOut" }}
                  />
                </clipPath>
                <clipPath id={`${clipId}-2`}>
                  <motion.rect
                    x={0}
                    y={30}
                    height={ENTRY_HEIGHT - 30}
                    initial={{ width: 0 }}
                    animate={{ width: ENTRY_VIEW_WIDTH + 10 }}
                    transition={{ duration: 1.15, ease: "easeOut", delay: 1.15 }}
                  />
                </clipPath>
              </defs>
              <g clipPath={`url(#${clipId}-1)`}>{line1}</g>
              <g clipPath={`url(#${clipId}-2)`}>{line23}</g>
            </>
          ) : (
            <>
              {line1}
              {line23}
            </>
          )}
        </g>
      </svg>
    </div>
  );
}

/**
 * Team-name cell - one per manager column in the header row.
 * [Stabilization fix - Feb 2026] Rendered as SVG <text> (pre-sweep
 * known-good) with per-manager rotation seeded by user_id (§E.3) and a
 * `textLength` compression safety net for names too long for the
 * column. Budget renders under the name, centered, when showBudget
 * (auction only).
 */
function WhiteboardTeamName({ mgr, showBudget }) {
  const name = (mgr.team_name || mgr.user_name || "").toUpperCase();
  const rot = seededSigned(mgr.user_id, "name") * 1.0;
  return (
    <div
      className="flex flex-col items-center justify-end gap-0.5"
      style={{ minHeight: HEADER_HEIGHT, position: "relative" }}
      data-testid={`whiteboard-team-${mgr.user_id}`}
    >
      <svg
        width="100%"
        height={32}
        viewBox={`0 0 ${HEADER_VIEW_WIDTH} 32`}
        preserveAspectRatio="xMidYMid meet"
        style={{ overflow: "visible" }}
      >
        <g transform={`rotate(${rot.toFixed(2)} ${HEADER_VIEW_WIDTH / 2} 16)`}>
          <text
            x={HEADER_VIEW_WIDTH / 2}
            y={22}
            textAnchor="middle"
            fill={WHITEBOARD_HEADER_RED}
            style={{ fontFamily: MARKER_FONT, fontSize: 26, letterSpacing: "0.05em", textTransform: "uppercase" }}
            {...svgFitProps(name, 26, HEADER_VIEW_WIDTH - 12)}
          >
            {name}
          </text>
        </g>
      </svg>
      {showBudget && (
        <div
          className="text-center"
          style={{ fontFamily: MARKER_FONT, fontSize: 15, color: "#2E6B2E" }}
        >
          ${mgr.budget ?? 0}
        </div>
      )}
    </div>
  );
}

// Ghost brush streaks - SVG <pattern> repeated across the surface at ~4%.
const GHOST_STREAKS_SVG = encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='260' height='180'>
    <g stroke='#9BA1A8' stroke-linecap='round' fill='none' opacity='0.045'>
      <path d='M 10 30 Q 80 20, 160 40 T 250 55' stroke-width='2.5'/>
      <path d='M 20 90 Q 90 100, 170 85 T 250 105' stroke-width='1.8'/>
      <path d='M 5 140 Q 100 130, 180 150 T 250 160' stroke-width='2.2'/>
      <path d='M 40 60 L 90 68' stroke-width='1.2'/>
      <path d='M 130 120 L 200 128' stroke-width='1.4'/>
    </g>
  </svg>`
);

/**
 * Restrained ambient marks (§E.8): one smudge, one dust dot, one ghost
 * word. Positions seeded by leagueId so they stay put per league.
 */
function AmbientMarks({ leagueId, isDark }) {
  const key = String(leagueId || "static");
  // Positions expressed as percentages so the marks scale with the surface.
  const smudgeX = 20 + (seededHash(key + ":smudgeX") % 45); // 20-65%
  const smudgeY = 45 + (seededHash(key + ":smudgeY") % 30); // 45-75%
  const dustX = 70 + (seededHash(key + ":dustX") % 25);     // 70-95%
  const dustY = 15 + (seededHash(key + ":dustY") % 40);     // 15-55%
  const ghostX = 55 + (seededHash(key + ":ghostX") % 30);   // 55-85%
  const ghostY = 78 + (seededHash(key + ":ghostY") % 15);   // 78-93%
  const smudgeColor = isDark ? "rgba(50,45,30,0.08)" : "rgba(0,0,0,0.08)";
  const dustColor = isDark ? "rgba(50,45,30,0.15)" : "rgba(0,0,0,0.15)";
  const ghostColor = isDark ? "rgba(50,45,30,0.04)" : "rgba(0,0,0,0.04)";
  return (
    <div
      className="absolute inset-0 pointer-events-none"
      data-testid="whiteboard-ambient-marks"
      style={{ overflow: "hidden" }}
    >
      <div
        style={{
          position: "absolute",
          left: `${smudgeX}%`,
          top: `${smudgeY}%`,
          width: 40,
          height: 8,
          borderRadius: "50%",
          background: smudgeColor,
          filter: "blur(3px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: `${dustX}%`,
          top: `${dustY}%`,
          width: 3,
          height: 3,
          borderRadius: "50%",
          background: dustColor,
        }}
      />
      <p
        className="uppercase m-0"
        style={{
          position: "absolute",
          left: `${ghostX}%`,
          top: `${ghostY}%`,
          color: ghostColor,
          fontFamily: MARKER_FONT,
          fontSize: 22,
          letterSpacing: "0.05em",
          transform: "rotate(-1.5deg)",
        }}
      >
        Bye Byes
      </p>
    </div>
  );
}

/**
 * Corner wear marks - four small mix-blend-mode overlays that give the
 * frame a used-object feel.
 */
function CornerWear() {
  const corners = [
    { top: 0, left: 0, transform: "translate(-30%, -30%) rotate(15deg)" },
    { top: 0, right: 0, transform: "translate(30%, -30%) rotate(-20deg)" },
    { bottom: 0, left: 0, transform: "translate(-30%, 30%) rotate(-15deg)" },
    { bottom: 0, right: 0, transform: "translate(30%, 30%) rotate(20deg)" },
  ];
  return (
    <>
      {corners.map((c, i) => (
        <div
          key={`wear-${i}`}
          style={{
            position: "absolute",
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: "#000",
            opacity: 0.3,
            mixBlendMode: "multiply",
            pointerEvents: "none",
            ...c,
          }}
        />
      ))}
    </>
  );
}

export default function WhiteboardDraftBoard({
  orderedManagers,
  rounds,
  getCell,
  isDark,
  leagueId,
  showBid = true,
  showBudget = true,
}) {
  // §E.1 - wood frame color + surface.
  const woodDay = "linear-gradient(135deg, #4A3320 0%, #3E2818 45%, #2E1D10 100%)";
  const woodNight = "linear-gradient(135deg, #3A2513 0%, #2E1D10 45%, #1E1108 100%)";
  const surfaceDay = "#FFFFFF";
  const surfaceNight = "#F5EFE0";
  const surfaceBg = isDark ? surfaceNight : surfaceDay;

  // Compute picks per manager, in round order. Whiteboard columns stack
  // top-down as picks arrive - we mirror that by iterating rounds and
  // collecting any cell that exists at that round per manager.
  const columnPicks = orderedManagers.map((mgr) => {
    const picks = [];
    for (let r = 0; r < rounds.length; r++) {
      const cell = getCell(mgr.user_id, r);
      if (cell) picks.push(cell);
    }
    return { mgr, picks };
  });

  // §B.4 - Snapshot the set of sale IDs present at first render. IDs
  // outside this set represent new picks that arrived after mount and
  // reveal via the §E.7 write-in animation. Existing picks render static.
  const initialSaleIdsRef = useRef(null);
  if (initialSaleIdsRef.current === null) {
    const ids = new Set();
    for (const { picks } of columnPicks) {
      for (const p of picks) {
        ids.add(String(p.request_id || p.nomination_id || p.sale_id || p.player?.id || ""));
      }
    }
    initialSaleIdsRef.current = ids;
  }
  const shouldAnimateSale = (sale) => {
    const id = String(sale?.request_id || sale?.nomination_id || sale?.sale_id || sale?.player?.id || "");
    return !initialSaleIdsRef.current.has(id);
  };

  // [Snake Board Fixes item 7] The board renders the FULL roster depth from
  // the start - one slot per round in every column - so all round slots are
  // visible immediately instead of the surface growing as picks land.
  const slotCount = Math.max(MIN_COL_ENTRIES, rounds.length);
  const surfaceMinHeight =
    HEADER_HEIGHT + UNDERSCORE_HEIGHT + ENTRY_HEIGHT * slotCount + 24;

  const colCount = orderedManagers.length;
  const gridCols = `repeat(${colCount}, minmax(120px, 1fr))`;

  // §E.9 night-mode vignette - inset radial darkening at edges.
  const vignette = isDark
    ? "radial-gradient(ellipse at center, rgba(0,0,0,0) 55%, rgba(0,0,0,0.18) 100%)"
    : "none";

  return (
    <div
      data-testid="whiteboard-draft-board"
      className="w-full relative"
      style={{
        background: isDark ? woodNight : woodDay,
        padding: FRAME_PADDING,
        borderRadius: 4,
      }}
    >
      <CornerWear />
      <div
        data-testid="whiteboard-surface"
        className="relative w-full"
        style={{
          background: `${surfaceBg} url("data:image/svg+xml,${GHOST_STREAKS_SVG}") repeat`,
          borderRadius: 2,
          padding: "16px 14px 20px",
          minHeight: surfaceMinHeight,
          boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
        }}
      >
        <AmbientMarks leagueId={leagueId} isDark={isDark} />
        {/* Night-mode vignette overlay */}
        {isDark && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: vignette }}
          />
        )}

        {/* Team-name row + underscore - pinned together.
            [Snake Board Fixes item 1] Sticky inside the board's scroll pane
            so the manager columns stay identified while the pick entries
            scroll underneath. Carries the surface background so entries
            never show through it. */}
        <div
          className="sticky top-0"
          style={{ zIndex: 2, background: surfaceBg }}
        >
        <div
          data-testid="whiteboard-team-row"
          className="grid"
          style={{ gridTemplateColumns: gridCols, gap: 4, position: "relative", zIndex: 1, alignItems: "end" }}
        >
          {orderedManagers.map((mgr) => (
            <WhiteboardTeamName key={`wb-name-${mgr.user_id}`} mgr={mgr} showBudget={showBudget} />
          ))}
        </div>

        {/* Wobbly underscore - spans full grid width */}
        <div
          data-testid="whiteboard-underscore"
          style={{ height: UNDERSCORE_HEIGHT, position: "relative", zIndex: 1 }}
        >
          <svg
            width="100%"
            height={UNDERSCORE_HEIGHT}
            viewBox={`0 0 1000 ${UNDERSCORE_HEIGHT}`}
            preserveAspectRatio="none"
            style={{ display: "block" }}
          >
            <path
              d={underscorePath(1000)}
              stroke={WHITEBOARD_HEADER_RED}
              strokeWidth={3}
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </div>
        </div>

        {/* Per-column stacked sold picks. [Snake Board Fixes item 7] Every
            round slot is rendered from the start - filled slots show the
            pick, empty slots hold their space - so the board reads as the
            full board on pick 1, not a growing list. */}
        <div
          data-testid="whiteboard-columns"
          className="grid"
          style={{ gridTemplateColumns: gridCols, gap: 4, position: "relative", zIndex: 1 }}
        >
          {columnPicks.map(({ mgr, picks }) => (
            <div
              key={`wb-col-${mgr.user_id}`}
              data-testid={`whiteboard-column-${mgr.user_id}`}
              className="flex flex-col"
            >
              {picks.map((sale) => (
                <WhiteboardEntry
                  key={`wb-entry-${sale?.request_id || sale?.nomination_id || sale?.sale_id || sale?.player?.id}`}
                  sale={sale}
                  shouldAnimate={shouldAnimateSale(sale)}
                  showBid={showBid}
                />
              ))}
              {Array.from(
                { length: Math.max(0, slotCount - picks.length) },
                (_, i) => (
                  <div
                    key={`wb-empty-${mgr.user_id}-${i}`}
                    data-testid={`whiteboard-empty-slot-${mgr.user_id}-${picks.length + i + 1}`}
                    style={{ height: ENTRY_HEIGHT }}
                  />
                ),
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
